/**
 * Camada 1b · implementacao de IdentityProvider sobre o Supabase Auth.
 *
 * A INTERFACE NAO MUDOU, e isso e a prova de que o ADR 0004 valeu a pena: o Clerk saiu, o
 * Supabase entrou, e `IdentityProvider` continua com os mesmos cinco metodos. A tela de
 * login tambem nao mudou uma linha. Trocar de provedor foi escrever esta classe, e nao
 * revisar o sistema inteiro, que era exatamente o que a abstracao existia para permitir.
 *
 * MODO HEADLESS: nenhum componente de interface do Supabase e usado. A tela de login e
 * nossa, em duas colunas, com a identidade de cada produto vinda do ProductConfig.
 *
 * A CHAVE QUE ESTA CLASSE USA e a `anon`, publica por natureza e submetida ao Row Level
 * Security. A `service_role` NAO entra aqui e nem poderia: a regra de lint
 * `credencial/no-service-role` quebra o build se ela aparecer em qualquer caminho que nao
 * seja db/migrations ou control-plane/.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { MENSAGEM_NAO_AUTORIZADO } from './lista-de-permissao.js';

import type {
  IdentityProvider,
  MotivoFalhaLogin,
  ResultadoLogin,
  Sessao,
  UsuarioIdentidade,
} from '../identity-provider.js';

/**
 * Traduz o erro do Supabase para o nosso motivo fechado.
 *
 * A LISTA E CURTA DE PROPOSITO, e o que nao esta nela cai em 'desconhecido'. Espelhar
 * todos os codigos do provedor recriaria a dependencia que a interface existe para cortar.
 */
function traduzirErro(codigo: string | undefined, mensagem: string): MotivoFalhaLogin {
  if (codigo === 'invalid_credentials' || /invalid login credentials/i.test(mensagem)) {
    // "Senha errada" e "e-mail nao existe" viram o MESMO motivo, de proposito: distinguir
    // os dois entrega a quem ataca a lista de quem tem conta.
    return 'credencial_invalida';
  }
  if (codigo === 'email_not_confirmed' || /email not confirmed/i.test(mensagem)) {
    return 'email_nao_verificado';
  }
  if (codigo === 'over_request_rate_limit' || /rate limit/i.test(mensagem)) {
    return 'conta_bloqueada';
  }
  if (/mfa|factor/i.test(mensagem)) return 'mfa_requerido';
  return 'desconhecido';
}

/** Mensagem em portugues por motivo. Nunca a mensagem crua do provedor, que vem em ingles
 *  e as vezes entrega detalhe que ajuda quem esta atacando. */
const MENSAGEM: Record<MotivoFalhaLogin, string> = {
  /**
   * Vem IMPORTADA de `lista-de-permissao.ts`, e nao copiada. O mesmo texto precisa
   * aparecer nos dois caminhos de recusa (a tentativa por senha, barrada antes de descer
   * ao provedor, e a sessao do Google, barrada na volta): duas redacoes do mesmo problema
   * fazem o suporte receber dois relatos e procurar dois defeitos.
   */
  nao_autorizado: MENSAGEM_NAO_AUTORIZADO,
  // NESTE REPOSITÓRIO os textos são os do admin atual, palavra por palavra (`traduzirErro`,
  // `portal/admin.html:3342-3356`), por decisão do fundador em 19/09/2026 ("igualar ao admin
  // atual"). Só o segundo fator não tem par no legado e ficou o da plataforma. Divergência protegida
  // (`scripts/sincronizar-chassi.sh`).
  credencial_invalida: 'E-mail ou senha incorretos.',
  mfa_requerido: 'Confirme o segundo fator para concluir a entrada.',
  conta_bloqueada: 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.',
  email_nao_verificado: 'Confirme seu e-mail antes de entrar.',
  provedor_indisponivel: 'Erro de conexão. Verifique sua internet.',
  desconhecido: 'Algo deu errado. Tente novamente.',
};

export interface OpcoesSupabase {
  /** A URL do projeto. Distinta por ambiente: hml e prd sao dois projetos separados. */
  readonly url: string;
  /**
   * A chave `anon`. Publica por natureza, e vai para o bundle do navegador.
   *
   * Ela NAO da acesso a nada por si so: quem decide o que ela enxerga sao as politicas de
   * RLS. E por isso que o RLS e a autoridade, e nao a posse da chave.
   */
  readonly chaveAnonima: string;
}

/** O formato minimo da sessao que o provedor devolve. Declarado aqui em vez de importado
 *  do SDK: o tipo de la muda entre versoes menores, e so estes tres campos sao usados. */
interface SessaoBruta {
  readonly access_token: string;
  readonly expires_at?: number;
}

/** O formato minimo do usuario. Mesma razao do tipo acima. */
interface UsuarioBruto {
  readonly id: string;
  readonly email?: string;
  readonly user_metadata?: Record<string, unknown>;
}

export class SupabaseIdentityProvider implements IdentityProvider {
  readonly nome = 'supabase';

  private cliente: SupabaseClient | null = null;

  constructor(private readonly opcoes: OpcoesSupabase) {}

  async inicializar(): Promise<void> {
    // Uma instancia por aplicacao: duas disputariam o mesmo armazenamento de sessao, e o
    // sintoma disso e login que "as vezes" nao persiste.
    if (this.cliente) return;
    this.cliente = createClient(this.opcoes.url, this.opcoes.chaveAnonima, {
      auth: {
        // A sessao persiste e se renova sozinha: sem isto, a pessoa e deslogada a cada
        // recarga da pagina.
        persistSession: true,
        autoRefreshToken: true,
        // O token volta na URL depois do retorno do Google, e precisa ser consumido dali.
        detectSessionInUrl: true,
      },
    });
  }

  private exigirCliente(): SupabaseClient {
    if (!this.cliente) {
      throw new Error('SupabaseIdentityProvider: chame inicializar() antes de usar o provedor.');
    }
    return this.cliente;
  }

  async signInWithPassword(email: string, senha: string): Promise<ResultadoLogin> {
    const cliente = this.exigirCliente();
    try {
      const { data, error } = await cliente.auth.signInWithPassword({ email, password: senha });

      if (error) {
        const motivo = traduzirErro((error as { code?: string }).code, error.message);
        return { ok: false, motivo, mensagem: MENSAGEM[motivo] };
      }
      if (!data.session || !data.user) {
        return {
          ok: false,
          motivo: 'provedor_indisponivel',
          mensagem: MENSAGEM.provedor_indisponivel,
        };
      }

      return { ok: true, sessao: this.montarSessao(data.session, data.user) };
    } catch {
      // Falha de rede chega aqui com outro formato. Tratar sem ler o erro evita que uma
      // falha dentro do tratamento de erro apague a causa real.
      return {
        ok: false,
        motivo: 'provedor_indisponivel',
        mensagem: MENSAGEM.provedor_indisponivel,
      };
    }
  }

  async signInWithGoogle(urlRetorno: string): Promise<void> {
    const cliente = this.exigirCliente();
    // Sai da pagina. Quem termina o login e o getSession depois do retorno, e nao esta
    // chamada: por isso o retorno e void e nao ResultadoLogin.
    await cliente.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: urlRetorno },
    });
  }

  async signOut(): Promise<void> {
    const cliente = this.exigirCliente();
    await cliente.auth.signOut();
    // A regra do projeto e que sair mata a sessao INTEIRA. O que e do provedor saiu acima;
    // o que e nosso sai aqui.
    try {
      window.localStorage.removeItem('cx.sessao');
      window.sessionStorage.clear();
    } catch {
      // Armazenamento bloqueado lanca ao escrever, e isso nao pode impedir alguem de sair.
    }
  }

  async getSession(): Promise<Sessao | null> {
    const cliente = this.exigirCliente();
    const { data } = await cliente.auth.getSession();
    if (!data.session || !data.session.user) return null;
    return this.montarSessao(data.session, data.session.user);
  }

  async refresh(): Promise<Sessao | null> {
    const cliente = this.exigirCliente();
    const { data, error } = await cliente.auth.refreshSession();
    // Sessao acabou de vez: null, e nao excecao. O caminho e refazer o login, e isso e
    // evento esperado e nao condicao excepcional.
    if (error || !data.session || !data.session.user) return null;
    return this.montarSessao(data.session, data.session.user);
  }

  /**
   * Traduz o formato do provedor para o nosso.
   *
   * E aqui que a abstracao ganha o valor dela: o resto do sistema nunca ve um tipo do
   * Supabase. No dia da proxima troca de provedor, este metodo e o unico que muda.
   */
  private montarSessao(sessao: SessaoBruta, usuario: UsuarioBruto): Sessao {
    const identidade: UsuarioIdentidade = {
      // O `id` do Supabase Auth. E ele que a coluna `users.auth_provider_id` guarda, e e
      // por isso que ele nao e a chave primaria de User: trocar de provedor reescreve
      // aquela coluna numa migracao, e nada mais do schema muda.
      idExterno: usuario.id,
      email: usuario.email ?? '',
      nome: (usuario.user_metadata?.full_name as string | undefined) ?? null,
      urlFoto: (usuario.user_metadata?.avatar_url as string | undefined) ?? null,
    };

    return {
      usuario: identidade,
      token: sessao.access_token,
      // `expires_at` vem em segundos desde a epoca, e Date espera milissegundos. A conta e
      // feita aqui, e nao no consumidor, para ninguem repetir o fator 1000 errado. Sem o
      // campo, uma hora e o padrao do Supabase.
      expiraEm: sessao.expires_at
        ? new Date(sessao.expires_at * 1000)
        : new Date(Date.now() + 3_600_000),
    };
  }
}
